// ./src/utils/api/TravelService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Account } from "./AccountService";
import { Car } from "./CarService";
import { ExpenseTag } from "./ExpenseTagService";
import { Expense, ExpenseInput } from "./ExpenseService";
import { UploadedFile } from "./UploadedFileService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const TRAVEL_POINT_FIELDS = `
  id
  odometer
  whenDone
  location
  whereDone
  fuelInTank
  comments
  pointType

  # Address fields
  address1
  address2
  city
  postalCode
  stateProvince
  country
  countryId
  # Coordinates
  longitude
  latitude
  # Weather data (auto-populated by backend)
  weatherTempC
  weatherFeelsLikeC
  weatherConditionCode
  weatherConditionIcon
  weatherDescription
  weatherHumidityPct
  weatherPressureHpa
  weatherCloudPct
  weatherVisibilityM
  weatherWindSpeedMps
  weatherWindDirDeg
  weatherPrecipMm
  weatherUvIndex
  weatherProvider
  weatherFetchedAt
`;

const TRAVEL_FIELDS = `
  id
  accountId
  userId
  carId
  firstOdometer
  lastOdometer
  odometerIn
  distance
  distanceIn
  distanceKm
  firstRecordId
  lastRecordId
  firstDttm
  lastDttm
  purpose
  destination
  comments
  travelType
  isRoundTrip
  reimbursementRate
  reimbursementRateCurrency
  calculatedReimbursement
  activeMinutes
  totalMinutes
  trackingStatus
  currentSegmentId
  gpsDistance
  encodedPolyline
  routeUploadedFileId
  trackingStartedAt
  trackingEndedAt
  status
  version
  createdBy
  updatedBy
  createdAt
  updatedAt
  car {
    id
    label
  }
  user {
    id
    firstName
    lastName
  }
  tags {
    id
    tagName
    tagColor
  }
  firstRecord {
    ${TRAVEL_POINT_FIELDS}
  }
  lastRecord {
    ${TRAVEL_POINT_FIELDS}
  }
  routeUploadedFile {
    id
    sizeBytes
    name
    fileUrl
    thumbnailUrl
    mimeType
  }
`;

/**
 * Extended fields returned by the get query but not the list query.
 * Includes richer user/account data, audit info, and waypoints.
 */
const TRAVEL_GET_EXTENDED_FIELDS = `
  user {
    id
    firstName
    lastName
    email
  }
  account {
    id
    name
  }
  userCreated {
    id
    firstName
    lastName
  }
  userUpdated {
    id
    firstName
    lastName
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const TRAVEL_LIST_QUERY = gql`
  query TravelList($filter: TravelFilter, $params: PaginationAndSorting) {
    travelList(filter: $filter, params: $params) {
      code
      data {
        ${TRAVEL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

/**
 * Full get query — includes waypoints and extended fields.
 * Used by default and when waypoints are needed (ActiveTravelContext, TravelWaypointsDrawer).
 */
const TRAVEL_GET_QUERY = gql`
  query TravelGet($id: ID) {
    travelGet(id: $id) {
      code
      data {
        ${TRAVEL_FIELDS}
        ${TRAVEL_GET_EXTENDED_FIELDS}
        waypoints {
          ${TRAVEL_POINT_FIELDS}
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

/**
 * Lite get query — extended fields but no waypoints.
 * Used when only travel details are needed (TravelEditDrawer, etc.).
 */
const TRAVEL_GET_LITE_QUERY = gql`
  query TravelGet($id: ID) {
    travelGet(id: $id) {
      code
      data {
        ${TRAVEL_FIELDS}
        ${TRAVEL_GET_EXTENDED_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TRAVEL_GET_MANY_QUERY = gql`
  query TravelGetMany($ids: [ID]) {
    travelGetMany(ids: $ids) {
      code
      data {
        ${TRAVEL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const TRAVEL_CREATE_MUTATION = gql`
  mutation TravelCreate($params: TravelInput) {
    travelCreate(params: $params) {
      code
      data {
        ${TRAVEL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TRAVEL_UPDATE_MUTATION = gql`
  mutation TravelUpdate($where: TravelWhereInput, $params: TravelInput) {
    travelUpdate(where: $where, params: $params) {
      code
      data {
        ${TRAVEL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TRAVEL_REMOVE_MUTATION = gql`
  mutation TravelRemove($where: TravelWhereInput) {
    travelRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TRAVEL_REMOVE_MANY_MUTATION = gql`
  mutation TravelRemoveMany($where: [TravelWhereInput]) {
    travelRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TRAVEL_PAUSE_MUTATION = gql`
  mutation TravelPause($where: TravelWhereInput) {
    travelPause(where: $where) {
      code
      data {
        ${TRAVEL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const TRAVEL_RESUME_MUTATION = gql`
  mutation TravelResume($where: TravelWhereInput) {
    travelResume(where: $where) {
      code
      data {
        ${TRAVEL_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface Travel {
  id?: string | undefined;
  accountId?: string | undefined;
  userId?: string | undefined;
  carId?: string | undefined;

  // Odometer tracking
  /** Odometer reading at trip start */
  firstOdometer?: number | undefined;
  /** Odometer reading at trip end */
  lastOdometer?: number | undefined;
  /** Unit for odometer values: km or mi */
  odometerIn?: string | undefined;

  // Distance tracking
  /** Trip distance (calculated from odometers or entered manually) */
  distance?: number | undefined;
  /** Unit for distance value: km or mi */
  distanceIn?: string | undefined;
  /** Trip distance converted to kilometers (for reporting) */
  distanceKm?: number | undefined;

  // Record references
  /** ID of the first travel point (expense_base record) */
  firstRecordId?: string | undefined;
  /** ID of the last travel point (expense_base record) */
  lastRecordId?: string | undefined;

  // Time tracking
  /** Trip start date and time */
  firstDttm?: string | undefined;
  /** Trip end date and time */
  lastDttm?: string | undefined;

  // Travel details
  /** Purpose of the trip (e.g., Client meeting, Delivery) */
  purpose?: string | undefined;
  /** Trip destination name or description */
  destination?: string | undefined;
  /** Additional notes about the travel */
  comments?: string | undefined;

  // Tax categorization
  /** Tax category: business, personal, medical, charity, commute */
  travelType?: string | undefined;
  /** Whether this is a round trip (same route back) */
  isRoundTrip?: boolean | undefined;

  // Reimbursement tracking
  /** Mileage reimbursement rate per distance unit */
  reimbursementRate?: number | undefined;
  /** Currency for reimbursement rate (ISO 4217: USD, CAD, EUR) */
  reimbursementRateCurrency?: string | undefined;
  /** Calculated reimbursement amount (rate × distance) */
  calculatedReimbursement?: number | undefined;

  // Time tracking (for profitability)
  /** Active driving/working time in minutes */
  activeMinutes?: number | undefined;
  /** Total trip time including waiting in minutes */
  totalMinutes?: number | undefined;

  /** Live tracking status: 0=none, 1=active, 2=paused, 3=completed */
  trackingStatus?: TrackingStatusValue | undefined;
  /** Current segment counter (incremented on each resume after pause) */
  currentSegmentId?: number | undefined;
  /** Total GPS-derived distance in km (from client running total) */
  gpsDistance?: number | undefined;
  /** Google Encoded Polyline of the simplified route */
  encodedPolyline?: string | undefined;
  /** Reference to ms_storage uploaded file containing the static route map PNG */
  routeUploadedFileId?: string | undefined;
  /** When live tracking was first started */
  trackingStartedAt?: string | undefined;
  /** When live tracking was stopped or completed */
  trackingEndedAt?: string | undefined;

  routeUploadedFile?: UploadedFile | null | undefined;

  // Audit fields
  /** Record status: 100=In Progress, 200=Completed, 300=Submitted, etc. */
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;

  // References
  user?: User | undefined;
  account?: Account | undefined;
  car?: Car | undefined;
  tags?: ExpenseTag[] | undefined;
  /** First travel waypoint */
  firstRecord?: Expense | undefined;
  /** Last travel waypoint */
  lastRecord?: Expense | undefined;
  /** All travel waypoints */
  waypoints?: Expense[] | undefined;
  userCreated?: User | undefined;
  userUpdated?: User | undefined;
}

export interface TravelInput {
  /** Vehicle ID for this travel */
  carId?: string | null | undefined;

  // Distance tracking (manual entry when odometer not used)
  /** Manual distance entry (when odometer not available) */
  distance?: number | null | undefined;
  /** Unit for manual distance: km or mi */
  distanceIn?: string | null | undefined;

  // Travel details
  /** Purpose of the trip */
  purpose?: string | null | undefined;
  /** Trip destination */
  destination?: string | null | undefined;
  /** Additional notes */
  comments?: string | null | undefined;

  // Tax categorization
  /** Tax category: business, personal, medical, charity, commute */
  travelType?: string | null | undefined;
  /** Whether this is a round trip */
  isRoundTrip?: boolean | null | undefined;

  // Reimbursement tracking
  /** Mileage reimbursement rate per distance unit */
  reimbursementRate?: number | null | undefined;
  /** Currency for reimbursement rate (ISO 4217) */
  reimbursementRateCurrency?: string | null | undefined;

  // Time tracking (for profitability)
  /** Active driving/working time in minutes */
  activeMinutes?: number | null | undefined;
  /** Total trip time in minutes */
  totalMinutes?: number | null | undefined;

  // Tags
  /** Array of tag IDs to associate with this travel */
  tagIds?: string[] | null | undefined;

  /** Record status */
  status?: number | null | undefined;

  /** First waypoint information like odometer, place and location. Used only when you start a trip or when user enters past trips details */
  firstRecord?: ExpenseInput | null | undefined;
  /** Last waypoint information like odometer, place and location. Used only when you close a trip or when user enters past trips details */
  lastRecord?: ExpenseInput | null | undefined;

  // Save the place. If true then use whereDone, location, address1, address2 and other address related fields to create a record in saved placed and use its reference
  savePlace?: boolean | null | undefined;
  // If true then try to lookup a saved place by coordinates and use its ID as savedPlaceId and fill out whereDone, location, address1 and other places
  lookupSavedPlaceByCoordinates?: boolean | null | undefined;

  /** Enable live GPS tracking when starting this travel (used on create only) */
  enableTracking?: boolean | null | undefined;
}

export interface TravelFilter {
  /** Filter by travel ID(s) */
  id?: string[] | null | undefined;
  /** Filter by account ID(s) */
  accountId?: string[] | null | undefined;
  /** Filter by user ID(s) who performed the travel */
  userId?: string[] | null | undefined;
  /** Filter by vehicle ID(s) */
  carId?: string[] | null | undefined;
  /** Filter by purpose (exact match) */
  purpose?: string[] | null | undefined;
  /** Filter by destination (exact match) */
  destination?: string[] | null | undefined;

  // Tax/type filters
  /** Filter by tax category: business, personal, medical, charity, commute */
  travelType?: string[] | null | undefined;
  /** Filter by round trip flag */
  isRoundTrip?: boolean | null | undefined;

  // Date range filters
  /** Filter trips starting on or after this date (ISO 8601) */
  firstDttmFrom?: string | null | undefined;
  /** Filter trips starting on or before this date (ISO 8601) */
  firstDttmTo?: string | null | undefined;
  /** Filter trips ending on or after this date (ISO 8601) */
  lastDttmFrom?: string | null | undefined;
  /** Filter trips ending on or before this date (ISO 8601) */
  lastDttmTo?: string | null | undefined;

  // Tag filter
  /** Filter by associated tag ID(s) */
  tagId?: string[] | null | undefined;

  /** Filter by record status */
  status?: number[] | null | undefined;
  /** Search in purpose and destination fields */
  searchKeyword?: string | null | undefined;

  trackingStatus?: TrackingStatusValue[] | null | undefined;
}

export interface TravelWhereInput {
  /** Travel ID to update or remove */
  id?: string | undefined;
}

/**
 * Options for controlling what data is returned by get/getMany queries.
 * Extensible — add more flags as needed (e.g., includeDocuments, includeExpenses).
 */
export interface TravelGetOptions {
  /**
   * Whether to include waypoints in the response.
   * Default: true (full query with waypoints).
   * Set to false for lighter queries when only travel details are needed.
   */
  includeWaypoints?: boolean;
}

export const TRACKING_STATUS = {
  NONE: 0,
  ACTIVE: 1,
  PAUSED: 2,
  COMPLETED: 3,
} as const;

export type TrackingStatusValue = typeof TRACKING_STATUS[keyof typeof TRACKING_STATUS];

// =============================================================================
// Service Class
// =============================================================================

/**
 * Travel service that handles all travel-related API calls.
 * Travels represent trips/journeys made with a vehicle, supporting:
 * - Odometer-based distance tracking
 * - Manual distance entry
 * - Tax categorization (business, personal, medical, charity, commute)
 * - Reimbursement tracking
 * - Waypoint management via linked expense records
 * - Live GPS tracking with pause/resume
 *
 * Supports CRUD operations: list, get, getMany, create, update, remove, removeMany
 * Tracking operations: pause, resume (start via create with enableTracking, stop via update with lastRecord)
 */
export class TravelService {
  /**
   * Get a list of travels with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: TravelFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(TRAVEL_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single travel by ID.
   *
   * By default returns full data including waypoints.
   * Pass `{ includeWaypoints: false }` for a lighter query
   * when only travel details are needed.
   */
  static async get(id: string, options?: TravelGetOptions): Promise<OpResult> {
    const query =
      options?.includeWaypoints === false
        ? TRAVEL_GET_LITE_QUERY
        : TRAVEL_GET_QUERY;

    const result = await ApiService.executeQuery(query, {
      id,
    });
    return result;
  }

  /**
   * Get multiple travels by IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(TRAVEL_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new travel record
   */
  static async create(params: TravelInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(TRAVEL_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing travel record
   */
  static async update(
    where: TravelWhereInput,
    params: TravelInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(TRAVEL_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete a travel record (soft delete)
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(TRAVEL_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple travel records (soft delete)
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      TRAVEL_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }

  /**
   * Pause live GPS tracking for a travel.
   * Tracking must be in ACTIVE state.
   */
  static async pause(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(TRAVEL_PAUSE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Resume live GPS tracking after a pause.
   * Tracking must be in PAUSED state.
   * Increments the segment counter so subsequent points belong to a new segment.
   */
  static async resume(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(TRAVEL_RESUME_MUTATION, {
      where: { id },
    });
    return result;
  }
}